import { getAddress, isAddressEqual, zeroAddress, type Address } from 'viem';

import { toast } from '@/components/ui/toast';
import { useSignInDialogStore } from '@/features/auth/signInDialogStore';
// the store module only, not the feature barrel: the barrel pulls the
// onboarding flow back into this module's own dependencies.
import { useOnboardingLauncher } from '@/features/onboarding/store';
import { performAccountLogout } from '@/features/tradingSession/logout';
import { useTradingSessionStore } from '@/features/tradingSession/store';
import { readAuthorizationWalletOnAnyAnchor } from '@/features/tradingSession/walletResolution';

/** Where the user ended up after switching over to another EOA. */
export type WalletSwitchRoute = 'reauthenticate' | 'onboarding' | 'sign-in';

export interface SwitchToAddressDependencies {
    logout: () => Promise<void>;
    lookupAccount: (owner: Address) => Promise<Address>;
    requestReauthentication: () => void;
    /** SPA navigation to the trading route, where onboarding is mounted. */
    goToTradingRoute: () => Promise<void>;
    launchOnboarding: () => void;
    openSignIn: () => void;
    notify: (message: string) => void;
    /**
     * Drops a social login's pending "give me an account" intent. It belongs to
     * the login being signed out of, and the launcher keeps it deliberately
     * sticky, so nothing else would.
     */
    clearSocialAssociation: () => void;
}

/** Onboarding is mounted on this route only, so a launch has to land here. */
const TRADING_ROUTE = '/';

const LOOKUP_FAILURE_MESSAGE =
    'Could not check that wallet for a Snappy account (network issue). Sign in again to continue.';

const defaultDependencies: SwitchToAddressDependencies = {
    logout: performAccountLogout,
    // a Snappy account lives on WHICHEVER anchor holds the owner's wallet, so
    // a Robinhood-only owner must not read as "no account"
    lookupAccount: readAuthorizationWalletOnAnyAnchor,
    // the EOA is already the one the wallet is on, so the window it opens is
    // the wallet ceremony and nothing else: no method picker to get lost in.
    requestReauthentication: () =>
        useTradingSessionStore.getState().requestConnectedWalletWindow(),
    // the router module is loaded on demand: this helper is imported by the
    // onboarding flow, which the router's own page tree imports back.
    goToTradingRoute: async () => {
        // already there (the provision step's own recovery panel runs this):
        // re-navigating would only push a redundant history entry.
        if (window.location.pathname === TRADING_ROUTE) return;
        const { default: router } = await import('@/router');
        await router.navigate(TRADING_ROUTE);
    },
    // same launch the sign-in dialog's "Create account" uses: a brand new
    // account is signed for by its owner EOA, so it can only go wallet-first.
    // `associateSocial: null` is explicit: the launcher only replaces a sticky
    // intent when the key is passed, and carrying a signed-out login's account
    // hash into the next owner's provisioning would bind it to their wallet.
    launchOnboarding: () =>
        useOnboardingLauncher.getState().launch('welcome', {
            resumeReauthOnFinish: true,
            walletFirst: true,
            associateSocial: null,
        }),
    openSignIn: () => useSignInDialogStore.getState().open(),
    notify: (message) => toast.error(message),
    clearSocialAssociation: () =>
        useOnboardingLauncher.getState().setAssociateSocial(null),
};

/**
 * Leaves the current account and continues as `next`: signs out, then routes
 * the user to whatever `next` needs (a trading window when that EOA already
 * owns a Snappy account, onboarding when it does not).
 *
 * A failed lookup must never be a dead end — the user is already signed out at
 * that point — so it falls back to the sign-in dialog, where they can pick a
 * route by hand, plus a toast saying why.
 *
 * Logout failures propagate: nothing was revoked, so the caller can surface the
 * error and leave the session as it was.
 *
 * The onboarding route is the one that is not global: the prompt runs from the
 * app shell (so it fires on the landing and legal pages too) while the gate that
 * consumes a launch request is mounted on the trading route alone. Launching
 * from anywhere else would sign the user out into nothing at all, so the
 * navigation happens first, and a navigation failure still launches rather than
 * stranding them.
 */
export async function resolveSwitchToAddress(
    next: Address,
    dependencies: SwitchToAddressDependencies = defaultDependencies,
): Promise<WalletSwitchRoute> {
    await dependencies.logout();
    // the intent belonged to the login just revoked, whichever route follows.
    dependencies.clearSocialAssociation();
    let account: Address;
    try {
        account = await dependencies.lookupAccount(getAddress(next));
    } catch {
        dependencies.notify(LOOKUP_FAILURE_MESSAGE);
        dependencies.openSignIn();
        return 'sign-in';
    }
    if (isAddressEqual(account, zeroAddress)) {
        try {
            await dependencies.goToTradingRoute();
        } catch {
            // the launch request lives in memory and is consumed whenever the
            // gate next mounts, so a failed navigation is a worse outcome than
            // this one, not a reason to leave the user with no next step.
        }
        dependencies.launchOnboarding();
        return 'onboarding';
    }
    dependencies.requestReauthentication();
    return 'reauthenticate';
}
